import axios from 'axios';
import { useAuthStore } from '../store/useAuthStore';

const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

// Set up axios response interceptor to handle 401 errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
      // Clear authentication state and redirect to login
      const url = error.response.config.url;
      if (url && !url.includes('/login') && error.response?.status === 401) {
        // Clear authentication state and redirect to login
        const { logout } = useAuthStore.getState();
        logout();
      }
    return Promise.reject(error);
  }
);

// Updated types to match the actual API response
export interface StreakResponse {
  success: boolean;
  data: {
    streak: number;
    streak_max: number;
    lastActiveDate: string;
    activityHistory: string[];
  };
}

export interface StreakStatsResponse {
  success: boolean;
  data: {
    currentStreak: number;
    maxStreak: number;
    totalActiveDays: number;
    recentActiveDays: number;
    averageActivity: number;
    daysSinceLastActivity: number;
    lastActiveDate: string;
    activityHistory: string[];
    isActiveToday: boolean;
  };
}

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  lastActivityDate: string | null;
  isActiveToday: boolean;
  totalDaysActive: number;
  activityHistory: string[];
}

export interface StreakStats {
  currentStreak: number;
  maxStreak: number;
  totalActiveDays: number;
  recentActiveDays: number;
  averageActivity: number;
  daysSinceLastActivity: number;
  lastActiveDate: string;
  activityHistory: string[];
  isActiveToday: boolean;
}

export interface UpdateActivityResponse {
  message: string;
  streakUpdated: boolean;
  newStreakCount: number;
  isFirstActivityToday: boolean;
}

export interface ResetStreakResponse {
  message: string;
  streakReset: boolean;
  previousStreak: number;
}

/**
 * Get user's current streak data
 * @returns Promise<StreakData>
 */
export const getUserStreak = async (): Promise<StreakData> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get<StreakResponse>(`${SERVER_URL}/api/product-user/streak`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    // Transform the API response to our internal format
    const apiData = response.data.data;
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

    return {
      currentStreak: apiData.streak,
      longestStreak: apiData.streak_max,
      lastActivityDate: apiData.lastActiveDate,
      isActiveToday: apiData.activityHistory.includes(today),
      totalDaysActive: apiData.activityHistory.length,
      activityHistory: apiData.activityHistory,
    };
  } catch (error) {
    console.error('Error fetching user streak:', error);
    throw error;
  }
};

/**
 * Update user activity (increment streak)
 * @returns Promise<UpdateActivityResponse>
 */
export const updateUserActivity = async (): Promise<UpdateActivityResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/update-activity`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error('Error updating user activity:', error);
    throw error;
  }
};

/**
 * Get detailed streak statistics
 * @returns Promise<StreakStats>
 */
export const getStreakStats = async (): Promise<StreakStats> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get<StreakStatsResponse>(`${SERVER_URL}/api/product-user/streak-stats`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching streak stats:', error);
    throw error;
  }
};



/**
 * Reset user's streak (for testing or admin purposes)
 * @returns Promise<ResetStreakResponse>
 */
export const resetUserStreak = async (): Promise<ResetStreakResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/reset-streak`, {}, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    return response.data;
  } catch (error) {
    console.error('Error resetting user streak:', error);
    throw error;
  }
};

/**
 * Check if user is active today
 * @returns Promise<boolean>
 */
export const isUserActiveToday = async (): Promise<boolean> => {
  try {
    const streakData = await getUserStreak();
    return streakData.isActiveToday;
  } catch (error) {
    console.error('Error checking if user is active today:', error);
    return false;
  }
};

/**
 * Get streak summary for display
 * @returns Promise<{ currentStreak: number; longestStreak: number; isActiveToday: boolean }>
 */
export const getStreakSummary = async () => {
  try {
    const streakData = await getUserStreak();
    return {
      currentStreak: streakData.currentStreak,
      longestStreak: streakData.longestStreak,
      isActiveToday: streakData.isActiveToday,
    };
  } catch (error) {
    console.error('Error fetching streak summary:', error);
    return {
      currentStreak: 0,
      longestStreak: 0,
      isActiveToday: false,
    };
  }
};
